/**
 * Modal components for the CRM.
 * All use useApiClient() (env-based base URL + token refresh) rather than a
 * hardcoded client, and post field names that match the backend schema.
 */

import React, { useState } from 'react';
import { numberInput, STEP_PER_LB } from '../lib/numberInput';
import { useAuth } from '../contexts/AuthContext';
import { useApiClient, useApi } from '../hooks/useApi';
import { MOVE_SIZES } from '../lib/moveSizes';
import { Icons } from '../lib/icons';
import { useAction } from '../hooks/useAction';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

// --- shared modal chrome ------------------------------------------------------
const overlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'var(--crm-overlay)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 300,
};
const sheet = (maxWidth: number): React.CSSProperties => ({
  background: 'var(--crm-panel)',
  border: '1px solid var(--crm-line)',
  borderRadius: '13px',
  width: '100%',
  maxWidth,
  maxHeight: '90vh',
  overflowY: 'auto',
});
const head: React.CSSProperties = {
  padding: '14px 18px',
  borderBottom: '1px solid var(--crm-line)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};
const formStyle: React.CSSProperties = { padding: '18px', display: 'flex', flexDirection: 'column', gap: '12px' };
const labelStyle: React.CSSProperties = { fontSize: '11px', color: 'var(--crm-muted)', fontWeight: 500 };
const inputStyle: React.CSSProperties = {
  padding: '8px 11px',
  background: 'var(--crm-panel-2)',
  border: '1px solid var(--crm-line)',
  borderRadius: '7px',
  color: 'var(--crm-text)',
  fontSize: '13px',
  boxSizing: 'border-box',
};
const footer: React.CSSProperties = {
  display: 'flex',
  gap: '12px',
  justifyContent: 'flex-end',
  marginTop: '12px',
  paddingTop: '12px',
  borderTop: '1px solid var(--crm-line)',
};
const cancelBtn: React.CSSProperties = { padding: '8px 16px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', color: 'var(--crm-muted)', borderRadius: '7px', cursor: 'pointer', fontSize: '12px' };
const saveBtn = (loading: boolean): React.CSSProperties => ({ padding: '8px 16px', background: loading ? 'var(--crm-line)' : 'var(--crm-accent-fill)', color: 'var(--crm-on-fill)', border: 'none', borderRadius: '7px', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 500 });
const closeX: React.CSSProperties = { background: 'none', border: 'none', color: 'var(--crm-muted)', cursor: 'pointer', fontSize: '20px', padding: 0 };
const field = (children: React.ReactNode): React.ReactNode => <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>{children}</div>;

const EMPTY_LEAD = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  move_type: 'Long Distance Relocation',
  move_size: '',
  weight_lbs: 0,
  cubic_feet: 0,
  binding: false,
  lead_source: '',
  origin_address: '',
  destination_address: '',
  move_date: '',
  stage: 'New Lead',
};

export function AddLeadModal({ isOpen, onClose, onSuccess }: ModalProps) {
  const api = useApiClient();
  const [formData, setFormData] = useState({ ...EMPTY_LEAD });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Settings → Lead sources, by name — the value intake writes to lead_source.
  const { data: sourceNames } = useApi<string[]>(async (client) => (await client.getLeadSourceNames()).data || []);

  const handleSubmitGuard = useAction(); // one at a time: a second click is ignored
  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); return handleSubmitGuard.run(() => handleSubmitNow(e)); };
  const handleSubmitNow = async (e: React.FormEvent) => {
    e.preventDefault();
    // A NAME, NOT TWO. The server accepts a one-word name — both name columns
    // are NOT NULL and createLead fills the missing one with '' — and intake
    // creates those leads routinely, because a web form asking for "Name" gets
    // one word often enough. A form that refused what the rest of the system
    // accepts just meant retyping the client's name with a full stop after it.
    if (!formData.first_name.trim()) {
      setError('A name is required');
      return;
    }
    if (!formData.lead_source) {
      setError('Pick a lead source');
      return;
    }
    setLoading(true);
    setError('');
    try {
      // Only send non-empty optional fields so we don't push '' into DATE columns.
      const payload: Record<string, unknown> = { ...formData };
      if (!payload.move_date) delete payload.move_date;
      if (!payload.move_size) delete payload.move_size;
      await api.createLead(payload);
      setFormData({ ...EMPTY_LEAD });
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to create lead');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={overlay}>
      <div style={sheet(620)}>
        <div style={head}>
          <h2 style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>New Lead</h2>
          <button onClick={onClose} style={closeX} aria-label="Close"><Icons.close size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} style={formStyle}>
          {error && (
            <div style={{ padding: '12px', background: 'var(--crm-red-tint)', border: '1px solid var(--crm-red-soft-line)', borderRadius: '7px', color: 'var(--crm-red)', fontSize: '12px' }}>{error}</div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {field(<><label style={labelStyle}>First name *</label><input style={inputStyle} value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} /></>)}
            {field(<><label style={labelStyle}>Last name</label><input style={inputStyle} value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} /></>)}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {field(<><label style={labelStyle}>Phone</label><input style={inputStyle} type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} /></>)}
            {field(<><label style={labelStyle}>Email</label><input style={inputStyle} type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} /></>)}
          </div>

          {field(
            <>
              <label style={labelStyle}>Lead source *</label>
              <select style={inputStyle} value={formData.lead_source} onChange={(e) => setFormData({ ...formData, lead_source: e.target.value })}>
                <option value="">{sourceNames ? 'Select…' : 'Loading…'}</option>
                {(sourceNames ?? []).map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
            {field(
              <>
                <label style={labelStyle}>Move type</label>
                <select style={inputStyle} value={formData.move_type} onChange={(e) => setFormData({ ...formData, move_type: e.target.value })}>
                  <option>Long Distance Relocation</option>
                  <option>Local Move</option>
                </select>
              </>
            )}
            {field(
              <>
                <label style={labelStyle}>Move size</label>
                <select style={inputStyle} value={formData.move_size} onChange={(e) => setFormData({ ...formData, move_size: e.target.value })}>
                  <option value="">Select…</option>
                  {MOVE_SIZES.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </>
            )}
            {field(<><label style={labelStyle}>Move date</label><input style={inputStyle} type="date" value={formData.move_date} onChange={(e) => setFormData({ ...formData, move_date: e.target.value })} /></>)}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {field(<><label style={labelStyle}>Weight (lbs)</label><input style={inputStyle} {...numberInput(1)} value={formData.weight_lbs} onChange={(e) => setFormData({ ...formData, weight_lbs: parseInt(e.target.value) || 0 })} /></>)}
            {field(<><label style={labelStyle}>CuFT</label><input style={inputStyle} {...numberInput(1)} value={formData.cubic_feet} onChange={(e) => setFormData({ ...formData, cubic_feet: parseInt(e.target.value) || 0 })} /></>)}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {field(<><label style={labelStyle}>Origin</label><textarea style={{ ...inputStyle, minHeight: '50px' }} value={formData.origin_address} onChange={(e) => setFormData({ ...formData, origin_address: e.target.value })} /></>)}
            {field(<><label style={labelStyle}>Destination</label><textarea style={{ ...inputStyle, minHeight: '50px' }} value={formData.destination_address} onChange={(e) => setFormData({ ...formData, destination_address: e.target.value })} /></>)}
          </div>

          <div style={footer}>
            <button type="button" onClick={onClose} style={cancelBtn}>Cancel</button>
            <button type="submit" disabled={loading} style={saveBtn(loading)}>{loading ? 'Saving…' : 'Save lead'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function AddBranchModal({ isOpen, onClose, onSuccess }: ModalProps) {
  const api = useApiClient();
  const [formData, setFormData] = useState({ name: '', province_state: '', tax_rate: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmitGuard = useAction(); // one at a time: a second click is ignored
  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); return handleSubmitGuard.run(() => handleSubmitNow(e)); };
  const handleSubmitNow = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) { setError('Branch name required'); return; }
    setLoading(true);
    setError('');
    try {
      await api.createBranch(formData);
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to create branch');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={overlay}>
      <div style={sheet(440)}>
        <div style={head}>
          <h2 style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>Add branch</h2>
          <button onClick={onClose} style={closeX} aria-label="Close"><Icons.close size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} style={formStyle}>
          {error && <div style={{ padding: '10px', background: 'var(--crm-red-tint)', border: '1px solid var(--crm-red-soft-line)', borderRadius: '7px', color: 'var(--crm-red)', fontSize: '12px' }}>{error}</div>}
          {field(<><label style={labelStyle}>Branch name</label><input style={inputStyle} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} /></>)}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {field(<><label style={labelStyle}>Province / State</label><input style={inputStyle} value={formData.province_state} onChange={(e) => setFormData({ ...formData, province_state: e.target.value })} /></>)}
            {field(<><label style={labelStyle}>Tax rate (%)</label><input style={inputStyle} {...numberInput(0.01)} value={formData.tax_rate} onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) || 0 })} /></>)}
          </div>
          <div style={footer}>
            <button type="button" onClick={onClose} style={cancelBtn}>Cancel</button>
            <button type="submit" disabled={loading} style={saveBtn(loading)}>{loading ? 'Saving…' : 'Save branch'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function AddTariffModal({ isOpen, onClose, onSuccess }: ModalProps) {
  const api = useApiClient();
  const [formData, setFormData] = useState({ name: '', pricing_method: 'weight', base_rate: 0, minimum_charge: 0 });
  // Hourly tariffs only: the default local deposit (056). Blank = $150.
  const [deposit, setDeposit] = useState('150');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmitGuard = useAction(); // one at a time: a second click is ignored
  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); return handleSubmitGuard.run(() => handleSubmitNow(e)); };
  const handleSubmitNow = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) { setError('Tariff name required'); return; }
    setLoading(true);
    setError('');
    try {
      await api.createTariff(formData.pricing_method === 'hourly' ? { ...formData, deposit_amount: deposit.trim() === '' ? null : Number(deposit) } : formData);
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to create tariff');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={overlay}>
      <div style={sheet(560)}>
        <div style={head}>
          <h2 style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>Add tariff</h2>
          <button onClick={onClose} style={closeX} aria-label="Close"><Icons.close size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} style={formStyle}>
          {error && <div style={{ padding: '10px', background: 'var(--crm-red-tint)', border: '1px solid var(--crm-red-soft-line)', borderRadius: '7px', color: 'var(--crm-red)', fontSize: '12px' }}>{error}</div>}
          {field(<><label style={labelStyle}>Tariff name</label><input style={inputStyle} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} /></>)}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {field(
              <>
                <label style={labelStyle}>Pricing method</label>
                <select style={inputStyle} value={formData.pricing_method} onChange={(e) => setFormData({ ...formData, pricing_method: e.target.value })}>
                  <option value="weight">By Weight (lbs)</option>
                  <option value="cuft">By Cubic Feet</option>
                  <option value="flat">Flat Rate</option>
                  <option value="hourly">Hourly</option>
                </select>
              </>
            )}
            {field(<><label style={labelStyle}>Base rate</label><input style={inputStyle} {...numberInput(formData.pricing_method === 'weight' ? STEP_PER_LB : 1)} value={formData.base_rate} onChange={(e) => setFormData({ ...formData, base_rate: parseFloat(e.target.value) || 0 })} /></>)}
          </div>
          {formData.pricing_method !== 'hourly' && field(<><label style={labelStyle}>Minimum charge</label><input style={inputStyle} {...numberInput(1)} value={formData.minimum_charge} onChange={(e) => setFormData({ ...formData, minimum_charge: parseFloat(e.target.value) || 0 })} /></>)}
          {formData.pricing_method === 'hourly' && field(<><label style={labelStyle}>Default deposit</label><input style={inputStyle} {...numberInput(1)} min="0" placeholder="150" value={deposit} onChange={(e) => setDeposit(e.target.value)} /></>)}
          <div style={footer}>
            <button type="button" onClick={onClose} style={cancelBtn}>Cancel</button>
            <button type="submit" disabled={loading} style={saveBtn(loading)}>{loading ? 'Saving…' : 'Save tariff'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function AddFeeModal({ isOpen, onClose, onSuccess }: ModalProps) {
  const api = useApiClient();
  const [formData, setFormData] = useState({ name: '', category: 'additional', fee_type: 'flat', amount: 0, percentage: 0, applied_to_base: 'subtotal' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmitGuard = useAction(); // one at a time: a second click is ignored
  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); return handleSubmitGuard.run(() => handleSubmitNow(e)); };
  const handleSubmitNow = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) { setError('Fee name required'); return; }
    setLoading(true);
    setError('');
    try {
      await api.createFee(formData);
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to create fee');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={overlay}>
      <div style={sheet(480)}>
        <div style={head}>
          <h2 style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>Add fee definition</h2>
          <button onClick={onClose} style={closeX} aria-label="Close"><Icons.close size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} style={formStyle}>
          {error && <div style={{ padding: '10px', background: 'var(--crm-red-tint)', border: '1px solid var(--crm-red-soft-line)', borderRadius: '7px', color: 'var(--crm-red)', fontSize: '12px' }}>{error}</div>}
          {field(<><label style={labelStyle}>Fee name</label><input style={inputStyle} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} /></>)}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {field(
              <>
                <label style={labelStyle}>Category</label>
                <select style={inputStyle} value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })}>
                  <option value="additional">Additional Fees</option>
                  <option value="materials">Materials</option>
                </select>
              </>
            )}
            {field(
              <>
                <label style={labelStyle}>Type</label>
                <select style={inputStyle} value={formData.fee_type} onChange={(e) => setFormData({ ...formData, fee_type: e.target.value })}>
                  <option value="flat">Flat fee ($)</option>
                  <option value="pct">Percentage (%)</option>
                </select>
              </>
            )}
          </div>
          {formData.fee_type === 'flat'
            ? field(<><label style={labelStyle}>Amount ($)</label><input style={inputStyle} {...numberInput(1)} value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })} /></>)
            : field(<><label style={labelStyle}>Percentage (%)</label><input style={inputStyle} {...numberInput(0.1)} value={formData.percentage} onChange={(e) => setFormData({ ...formData, percentage: parseFloat(e.target.value) || 0 })} /></>)}
          <div style={footer}>
            <button type="button" onClick={onClose} style={cancelBtn}>Cancel</button>
            <button type="submit" disabled={loading} style={saveBtn(loading)}>{loading ? 'Saving…' : 'Save fee'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function ScheduleFollowUpModal({ isOpen, onClose, onSuccess, leadId }: ModalProps & { leadId?: string }) {
  const api = useApiClient();
  const { user } = useAuth();
  const [formData, setFormData] = useState({ follow_up_date: '', follow_up_time: '', notes: '', pinned: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmitGuard = useAction(); // one at a time: a second click is ignored
  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); return handleSubmitGuard.run(() => handleSubmitNow(e)); };
  const handleSubmitNow = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.follow_up_date) { setError('Date required'); return; }
    if (!leadId || !user) { setError('Missing lead or user'); return; }
    setLoading(true);
    setError('');
    try {
      await api.createFollowUp({
        lead_id: leadId,
        assigned_to_user_id: user.id, // NOT NULL in schema
        follow_up_date: formData.follow_up_date,
        follow_up_time: formData.follow_up_time || undefined,
        description: formData.notes || undefined, // backend field is `description`
        pinned: formData.pinned, // pin to calendar (Phase 8h.2)
      });
      setFormData({ follow_up_date: '', follow_up_time: '', notes: '', pinned: false });
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to schedule follow-up');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={overlay}>
      <div style={sheet(480)}>
        <div style={head}>
          <h2 style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>Schedule follow-up</h2>
          <button onClick={onClose} style={closeX} aria-label="Close"><Icons.close size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} style={formStyle}>
          {error && <div style={{ padding: '10px', background: 'var(--crm-red-tint)', border: '1px solid var(--crm-red-soft-line)', borderRadius: '7px', color: 'var(--crm-red)', fontSize: '12px' }}>{error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {field(<><label style={labelStyle}>Date *</label><input style={inputStyle} type="date" value={formData.follow_up_date} onChange={(e) => setFormData({ ...formData, follow_up_date: e.target.value })} /></>)}
            {field(<><label style={labelStyle}>Time (optional)</label><input style={inputStyle} type="time" value={formData.follow_up_time} onChange={(e) => setFormData({ ...formData, follow_up_time: e.target.value })} /></>)}
          </div>
          {field(<><label style={labelStyle}>Description / Notes</label><textarea style={{ ...inputStyle, minHeight: '60px' }} value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Call back about pricing, follow up on deposit, etc." /></>)}
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
            <input type="checkbox" checked={formData.pinned} onChange={(e) => setFormData({ ...formData, pinned: e.target.checked })} style={{ marginTop: '2px' }} />
            <span>
              <span style={{ fontSize: '12px', color: 'var(--crm-text)' }}>Pin to calendar</span>
              <span style={{ display: 'block', fontSize: '10px', color: 'var(--crm-dim)' }}>Pinned follow-ups appear on the calendar for visibility</span>
            </span>
          </label>
          <div style={footer}>
            <button type="button" onClick={onClose} style={cancelBtn}>Cancel</button>
            <button type="submit" disabled={loading} style={saveBtn(loading)}>{loading ? 'Scheduling…' : 'Schedule'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- Edit Contact Details (N1) -----------------------------------------------
// Fixes the called-out gap: today there is no way to correct a misspelled name,
// changed phone, or wrong email. Contact details ONLY — the rest (weights,
// addresses, dates, move details) is edited on the estimate page / sticky boxes.
export function EditContactModal({
  isOpen,
  onClose,
  lead,
  onSaved,
}: {
  isOpen: boolean;
  onClose: () => void;
  lead: any;
  onSaved?: () => void;
}) {
  const api = useApiClient();
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '', email: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // G1b — Additional emails. These save IMMEDIATELY rather than on Save, because
  // adding one queues a 90-day backfill across every mailbox: tying that to a
  // form submit would fire it again on every unrelated edit to the name or phone.
  const [extras, setExtras] = useState<any[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [extraBusy, setExtraBusy] = useState(false);
  const [extraError, setExtraError] = useState('');
  const [extraNote, setExtraNote] = useState('');

  const loadExtras = React.useCallback(async () => {
    if (!lead?.id) return;
    try {
      setExtras((await api.getLeadEmailAddresses(lead.id)).data || []);
    } catch {
      // Migration 039 not run here, or a transient failure: the rest of the modal
      // still works and the section simply shows nothing.
      setExtras([]);
    }
  }, [api, lead?.id]);

  // Reload the draft whenever a different lead's modal opens.
  React.useEffect(() => {
    if (isOpen && lead) {
      setForm({
        first_name: lead.first_name ?? '',
        last_name: lead.last_name ?? '',
        phone: lead.phone ?? '',
        email: lead.email ?? '',
      });
      setError('');
      setExtraError('');
      setExtraNote('');
      setNewEmail('');
      setNewLabel('');
      void loadExtras();
    }
  }, [isOpen, lead, loadExtras]);

  const addExtraGuard = useAction(); // one at a time: a second click is ignored
  const addExtra = () => addExtraGuard.run(() => addExtraNow());
  const addExtraNow = async () => {
    const addr = newEmail.trim();
    if (!addr) return;
    setExtraBusy(true);
    setExtraError('');
    setExtraNote('');
    try {
      await api.addLeadEmailAddress(lead.id, addr, newLabel.trim() || undefined);
      setNewEmail('');
      setNewLabel('');
      await loadExtras();
      setExtraNote('Added. Mail from the last 90 days is being searched for it now — it appears on the Email tab within a minute or two.');
    } catch (e: any) {
      setExtraError(e?.message || 'Could not add that address.');
    } finally {
      setExtraBusy(false);
    }
  };

  const removeExtra = async (row: any) => {
    // Said out loud: removing the address stops FUTURE matching and leaves the
    // mail already on the file alone. Deleting a client's correspondence because
    // somebody tidied a list is not something this should do quietly.
    if (!window.confirm(`Stop matching mail to ${row.email}?\n\nEmail already on this file stays on it.`)) return;
    setExtraBusy(true);
    setExtraError('');
    setExtraNote('');
    try {
      await api.deleteLeadEmailAddress(row.id);
      await loadExtras();
    } catch (e: any) {
      setExtraError(e?.message || 'Could not remove that address.');
    } finally {
      setExtraBusy(false);
    }
  };

  const handleSubmitGuard = useAction(); // above the isOpen early return; one at a time: a second click is ignored
  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => { e.preventDefault(); return handleSubmitGuard.run(() => handleSubmitNow(e)); };
  const handleSubmitNow = async (e: React.FormEvent) => {
    e.preventDefault();
    /**
     * A NAME, NOT TWO — and this one was worse than the Add form.
     *
     * A lead that arrived from a web form with a one-word name has last_name
     * '', so opening Edit contact details and pressing Save failed on a field
     * the user had not touched: "First and last name are required", on a lead
     * the system itself created that way. There was no way to change the phone
     * number of such a lead without inventing a surname for them.
     */
    if (!form.first_name.trim()) {
      setError('A name is required');
      return;
    }
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError('Enter a valid email address');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await api.updateLead(lead.id, {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
      });
      onSaved?.();
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to save contact details');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={overlay}>
      <div style={sheet(520)}>
        <div style={head}>
          <h2 style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>Edit Contact Details</h2>
          <button onClick={onClose} style={closeX}>×</button>
        </div>
        <form style={formStyle} onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {field(<><label style={labelStyle}>First name *</label><input style={inputStyle} value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></>)}
            {field(<><label style={labelStyle}>Last name</label><input style={inputStyle} value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></>)}
          </div>
          {field(<><label style={labelStyle}>Phone</label><input style={inputStyle} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></>)}
          {field(<><label style={labelStyle}>Email</label><input style={inputStyle} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></>)}

          {/* ---- Additional emails (G1b) ---- */}
          <div style={{ borderTop: '1px solid var(--crm-line)', paddingTop: '12px' }}>
            <div style={{ ...labelStyle, marginBottom: '6px' }}>Additional emails</div>
            <div style={{ fontSize: '11px', color: 'var(--crm-dim)', marginBottom: '8px', lineHeight: 1.5 }}>
              A spouse or a work address. Mail to or from any of these lands on this file too.
              Saved as you add them, not on Save.
            </div>

            {extras.length === 0 && (
              <div style={{ fontSize: '11px', color: 'var(--crm-dim)', marginBottom: '8px' }}>None yet.</div>
            )}
            {extras.map((row) => (
              <div key={row.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--crm-panel-2)', borderRadius: '6px', padding: '6px 9px', marginBottom: '5px' }}>
                <span style={{ flex: 1, fontSize: '12px', color: 'var(--crm-text)', fontFamily: "'DM Mono', monospace", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row.email}
                </span>
                {row.label && <span style={{ fontSize: '11px', color: 'var(--crm-muted)' }}>{row.label}</span>}
                <button
                  type="button" disabled={extraBusy} onClick={() => removeExtra(row)}
                  title="Stop matching mail to this address"
                  style={{ background: 'none', border: 'none', color: 'var(--crm-red)', cursor: 'pointer', padding: '0 2px' }}
                >
                  <Icons.delete size={14} />
                </button>
              </div>
            ))}

            <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
              <input
                style={{ ...inputStyle, flex: 2 }} type="email" placeholder="name@example.com"
                value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void addExtra(); } }}
              />
              <input
                style={{ ...inputStyle, flex: 1 }} placeholder="label (optional)"
                value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void addExtra(); } }}
              />
              <button
                type="button" onClick={() => void addExtra()} disabled={extraBusy || !newEmail.trim()}
                style={{ padding: '8px 14px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', color: 'var(--crm-muted)', borderRadius: '7px', cursor: extraBusy ? 'default' : 'pointer', fontSize: '12px', flexShrink: 0 }}
              >
                {extraBusy ? '…' : 'Add'}
              </button>
            </div>
            {extraError && <div style={{ fontSize: '11px', color: 'var(--crm-red)', marginTop: '6px' }}>{extraError}</div>}
            {extraNote && <div style={{ fontSize: '11px', color: 'var(--crm-green)', marginTop: '6px', lineHeight: 1.5 }}>{extraNote}</div>}
          </div>

          {error && <div style={{ fontSize: '12px', color: 'var(--crm-red)' }}>{error}</div>}
          <div style={footer}>
            <button type="button" onClick={onClose} style={cancelBtn}>Cancel</button>
            <button type="submit" disabled={loading} style={saveBtn(loading)}>{loading ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
